import { Link } from "react-router-dom";
import { JWKS_URL } from "../lib/config";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ApiOverview() {
  return (
    <section data-testid="api-overview" className="space-y-10">
      <div className="space-y-3">
        <p className="hs-eyebrow">PUBLIC API · V1</p>
        <h1 className="hs-display text-3xl md:text-4xl">Lookup API</h1>
        <p className="text-muted max-w-3xl">
          Query confirmed malicious identifiers programmatically. Responses are signed with
          the HerdSafe Ed25519 key published at{" "}
          <code className="hs-mono text-xs">{JWKS_URL}</code>.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="hs-card">
          <div className="hs-eyebrow mb-1">AUTH</div>
          <code className="text-xs text-fg">Authorization: Bearer hs_live_…</code>
          <p className="mt-2 text-sm text-muted"><Link className="hs-link" to="/get-key">Get a free key</Link> with your email (no
            password). Keys are hashed in storage and shown once.</p>
        </div>
        <div className="hs-card">
          <div className="hs-eyebrow mb-1">PLANS</div>
          <ul className="text-xs text-fg space-y-0.5">
            <li>anonymous · 100/day/IP</li>
            <li>free · 10,000/month</li>
            <li>builder · 250,000/month</li>
            <li>scale · 5,000,000/month</li>
            <li>internal · unlimited</li>
          </ul>
        </div>
        <div className="hs-card">
          <div className="hs-eyebrow mb-1">VISIBILITY</div>
          <p className="text-xs text-fg">
            <code>confirmed</code> → malicious<br/>
            <code>suspected</code> → suspicious (scale+ only)<br/>
            <code>cleared</code> → clean_seen<br/>
            everything else → unknown
          </p>
        </div>
      </div>

      <div className="hs-card space-y-3" data-testid="api-overview-cli">
        <h2 className="text-fg">Scan a project's MCP servers</h2>
        <p className="text-sm text-muted max-w-3xl">
          <code>npx herdsafe scan</code> reads your MCP configs (mcp.json, .cursor, .vscode, Claude Desktop / Code, AGENTS.md)
          and prints what each server can do: where it runs, capabilities, secrets, contacted domains and warnings. It is
          read-only, open source (MIT) and sends only package names and remote URLs, never your tokens. Watch the results
          for risky changes on your <Link className="hs-link" to="/watchlist">watchlist</Link>.
        </p>
        <pre className="hs-code" style={{ whiteSpace: "pre-wrap" }}>{`npx herdsafe scan
npx herdsafe scan --json   # paste into the watchlist`}</pre>
        <p className="text-sm text-muted">Under the hood: <code>POST /api/v1/servers/resolve</code> with
          <code>{` {"servers": [{"key": "0", "package": "npm:@scope/name"}]}`}</code>; each server counts as one lookup.</p>
      </div>

      <div className="hs-card space-y-3" data-testid="api-overview-curl">
        <h2 className="text-fg">cURL example</h2>
        <pre className="hs-code" style={{ whiteSpace: "pre-wrap" }}>
{`curl -sS "${BACKEND || "https://herdsafe.ai"}/api/v1/lookup?type=npm_package&id=evil-pkg" \\
  -H "Authorization: Bearer hs_live_YOUR_KEY" \\
  -D -`}
        </pre>
        <p className="text-xs text-muted">
          Response headers include <code>X-HerdSafe-Signature</code>, <code>X-HerdSafe-Key-Id</code>,
          and IETF rate-limit headers (<code>RateLimit-Limit</code>, <code>RateLimit-Remaining</code>,
          <code>RateLimit-Reset</code>).
        </p>
      </div>

      <div className="hs-card space-y-3" data-testid="api-overview-verify">
        <h2 className="text-fg">Verify a response (Node / Python)</h2>
        <pre className="hs-code" style={{ whiteSpace: "pre-wrap" }}>
{`# fetch the active key JWK
curl -s "${JWKS_URL}"

# Node
import crypto from "node:crypto";
const jwk = (await (await fetch("${JWKS_URL}")).json()).keys[0];
const pub = crypto.createPublicKey({ key: jwk, format: "jwk" });
crypto.verify(null, Buffer.from(body), pub, Buffer.from(sig, "base64url"));

# Python
from cryptography.hazmat.primitives.asymmetric.ed25519 import Ed25519PublicKey
import base64, json, requests
jwk = requests.get("${JWKS_URL}").json()["keys"][0]
pub = Ed25519PublicKey.from_public_bytes(
    base64.urlsafe_b64decode(jwk["x"] + "=" * (-len(jwk["x"]) % 4))
)
pub.verify(sig_bytes, body_bytes)`}
        </pre>
      </div>

      <div>
        <h2 className="text-fg mb-2">Full reference</h2>
        <p className="text-muted">
          OpenAPI 3.1 spec:{" "}
          <a className="hs-link" data-testid="api-overview-openapi-link"
             href={`${BACKEND}/api/v1/openapi.json`}>/api/v1/openapi.json</a>
        </p>
        <p className="text-muted">
          Swagger UI:{" "}
          <a className="hs-link" data-testid="api-overview-swagger-link"
             href={`${BACKEND}/api/docs/api`}>/api/docs/api</a>
        </p>
        <p className="text-muted pt-2">
          Need a key? Email the HerdSafe admin or see the{" "}
          <Link to="/disclosure-policy" className="hs-link">disclosure policy</Link> first.
        </p>
      </div>
    </section>
  );
}
